import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import PageTitle from '../components/PageTitle';
import { 
  Calendar,
  Clock,
  Play,
  ChevronRight,
  CheckCircle,
  Lock,
  Star,
  Target,
  Flame,
  Loader2,
  ArrowLeft,
  LogOut,
  User,
  Settings,
  Gift
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const ProgramsPage = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { api, isAuthenticated, user, logout } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [userProgress, setUserProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    if (programId) {
      fetchProgramDetail();
    } else {
      fetchPrograms();
    }
  }, [programId]);

  const fetchPrograms = async () => {
    setLoading(true);
    try {
      const res = await api.get('/programs/');
      setPrograms(res.data);
    } catch (error) {
      console.error('Failed to fetch programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgramDetail = async () => {
    setLoading(true);
    try {
      const [programRes, progressRes] = await Promise.all([
        api.get(`/programs/${programId}`),
        isAuthenticated ? api.get(`/programs/${programId}/progress`).catch(() => ({ data: null })) : Promise.resolve({ data: null })
      ]);
      setSelectedProgram(programRes.data);
      setUserProgress(progressRes.data);
    } catch (error) {
      console.error('Failed to fetch program:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartProgram = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to start a program');
      return;
    }
    
    try {
      await api.post(`/programs/${programId}/start`);
      toast.success('Program started! Let\'s begin your journey.');
      fetchProgramDetail();
    } catch (error) {
      toast.error('Failed to start program');
    }
  };

  const handleCompleteClass = async (classId) => {
    try {
      const res = await api.post(`/programs/${programId}/complete-class/${classId}`);
      toast.success(`Progress: ${res.data.completion_percent}%`);
      fetchProgramDetail();
    } catch (error) {
      toast.error('Failed to mark class as complete');
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-400 bg-green-400/10';
      case 'intermediate': return 'text-yellow-400 bg-yellow-400/10';
      case 'advanced': return 'text-red-400 bg-red-400/10';
      default: return 'text-zinc-400 bg-zinc-400/10';
    }
  };

  // Program Detail View
  if (programId && selectedProgram) {
    const completedClasses = userProgress?.completed_classes || [];
    const isEnrolled = userProgress?.started_at;

    return (
      <div className="min-h-screen bg-[#090D0F]">
        <PageTitle title={selectedProgram.title} description={selectedProgram.description} />
        <header className="sticky top-0 z-50 glass border-b border-zinc-800">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Logo linkTo="/dashboard" size="default" />
            <Link to="/programs">
              <Button variant="ghost" className="text-zinc-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                All Programs
              </Button>
            </Link>
          </div>
        </header>

        <main className="max-w-5xl mx-auto px-6 py-12">
          {/* Program Header */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="aspect-video rounded-sm overflow-hidden">
              <img 
                src={selectedProgram.thumbnail_url} 
                alt={selectedProgram.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <span className={`inline-block px-3 py-1 rounded-sm text-xs font-bold uppercase ${getDifficultyColor(selectedProgram.difficulty)}`}>
                {selectedProgram.difficulty}
              </span>
              <h1 className="text-3xl font-bold mt-4 mb-4">{selectedProgram.title}</h1>
              <p className="text-zinc-400 mb-6">{selectedProgram.description}</p>
              
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-[#121416] rounded-sm">
                  <Calendar className="w-5 h-5 text-teal-400 mx-auto mb-2" />
                  <p className="text-xl font-bold">{selectedProgram.duration_weeks}</p>
                  <p className="text-xs text-zinc-500">Weeks</p>
                </div>
                <div className="text-center p-4 bg-[#121416] rounded-sm">
                  <Target className="w-5 h-5 text-teal-400 mx-auto mb-2" />
                  <p className="text-xl font-bold">{selectedProgram.classes_per_week}</p>
                  <p className="text-xs text-zinc-500">Classes/Week</p>
                </div>
                <div className="text-center p-4 bg-[#121416] rounded-sm">
                  <Play className="w-5 h-5 text-teal-400 mx-auto mb-2" />
                  <p className="text-xl font-bold">{selectedProgram.total_classes}</p>
                  <p className="text-xs text-zinc-500">Total Classes</p>
                </div>
              </div>

              {isEnrolled ? (
                <div>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-zinc-400">Progress</span>
                      <span className="text-teal-400 font-bold">{userProgress.completion_percent}%</span>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-teal-400 transition-all"
                        style={{ width: `${userProgress.completion_percent}%` }}
                      />
                    </div>
                  </div>
                  <Button className="w-full bg-teal-400 text-black font-bold hover:bg-teal-300">
                    Continue Program
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={handleStartProgram}
                  className="w-full bg-teal-400 text-black font-bold hover:bg-teal-300"
                >
                  Start Program
                </Button>
              )}
            </div>
          </div>

          {/* Class List */}
          <div>
            <h2 className="text-2xl font-bold mb-6">PROGRAM CLASSES</h2>
            <div className="space-y-4">
              {selectedProgram.classes?.map((pc, index) => {
                const classDetail = pc.class_details;
                const isCompleted = completedClasses.includes(pc.class_id);
                const isLocked = !isEnrolled && index > 0;

                return (
                  <div 
                    key={pc.class_id}
                    className={`bg-[#121416] border border-zinc-800 rounded-sm p-4 flex items-center gap-4 ${isLocked ? 'opacity-50' : ''}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isCompleted ? 'bg-green-400/20 text-green-400' : 'bg-zinc-800 text-zinc-400'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : isLocked ? (
                        <Lock className="w-5 h-5" />
                      ) : (
                        <span className="font-bold">{index + 1}</span>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <p className="font-medium">{classDetail?.title || `Class ${index + 1}`}</p>
                      <p className="text-sm text-zinc-500">
                        {classDetail?.duration_minutes || 45} min • {classDetail?.difficulty || 'intermediate'}
                      </p>
                    </div>

                    {!isLocked && classDetail && (
                      <div className="flex items-center gap-2">
                        {isEnrolled && !isCompleted && (
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => handleCompleteClass(pc.class_id)}
                            className="border-zinc-700"
                          >
                            Mark Complete
                          </Button>
                        )}
                        <Link to={`/class/${pc.class_id}`}>
                          <Button size="sm" className="bg-teal-400 text-black hover:bg-teal-300">
                            <Play className="w-4 h-4 mr-1" /> Watch
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Programs List View
  return (
    <div className="min-h-screen bg-[#090D0F]">
      <PageTitle title="Programs" description="Structured multi-week Lagree programs designed to help you achieve your fitness goals." />
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo linkTo="/dashboard" size="default" />
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/dashboard" className="text-zinc-400 hover:text-white">Home</Link>
            <Link to="/classes" className="text-zinc-400 hover:text-white">Classes</Link>
            <Link to="/programs" className="text-white font-medium">Programs</Link>
            <Link to="/shop" className="text-zinc-400 hover:text-white">Shop</Link>
            <Link to="/instructors" className="text-zinc-400 hover:text-white">Instructors</Link>
          </nav>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                <div className="w-8 h-8 bg-teal-400 rounded-full flex items-center justify-center text-sm font-bold text-black">
                  {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                </div>
                <span className="hidden md:inline text-zinc-300">{user?.first_name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-[#121212] border-zinc-800">
              <DropdownMenuItem asChild>
                <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                  <User className="w-4 h-4" />
                  Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/referrals" className="flex items-center gap-2 cursor-pointer text-teal-400">
                  <Gift className="w-4 h-4" />
                  Refer & Earn $50
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                  <Settings className="w-4 h-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="flex items-center gap-2 text-red-400 cursor-pointer"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">CURATED PROGRAMS</h1>
          <p className="text-zinc-500 max-w-2xl mx-auto">
            Structured multi-week programs designed to help you achieve specific fitness goals. 
            Follow along with our expert instructors and track your progress.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {programs.map((program) => (
              <Link key={program.id} to={`/programs/${program.id}`}>
                <div className="bg-[#121416] border border-zinc-800 rounded-sm overflow-hidden hover:border-teal-400/50 transition-colors group">
                  <div className="aspect-video relative overflow-hidden">
                    <img 
                      src={program.thumbnail_url} 
                      alt={program.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {program.is_featured && (
                      <span className="absolute top-4 left-4 px-3 py-1 bg-teal-400 text-black text-xs font-bold uppercase rounded-sm">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`px-2 py-1 rounded-sm text-xs font-bold uppercase ${getDifficultyColor(program.difficulty)}`}>
                        {program.difficulty}
                      </span>
                      <span className="text-xs text-zinc-500">{program.duration_weeks} weeks</span>
                    </div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-teal-400 transition-colors">
                      {program.title}
                    </h3>
                    <p className="text-zinc-500 text-sm mb-4 line-clamp-2">{program.description}</p>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-4 text-zinc-400">
                        <span className="flex items-center gap-1">
                          <Play className="w-4 h-4" /> {program.total_classes} classes
                        </span>
                        <span className="flex items-center gap-1">
                          <Target className="w-4 h-4" /> {program.classes_per_week}/week
                        </span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-teal-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ProgramsPage;
